import type {
  IssueTypeField,
  UIFieldDefinition
} from '../prepareSendIssueFields';
import {
  IssueFieldSource,
  type MappingDefinition
} from './MappingSchemaModels';
import {
  JiraFieldMappersKeys,
  type UIFieldInputType,
  type UiFieldValidationError,
  type JiraFieldMapper
} from './MapperInterface';
import { defaultWithoutError } from './ShortTextMapper';

const JiraTypeDate = 'date';
const JiraTypeDateTime = 'datetime';

export class DateMapper implements JiraFieldMapper {
  id = JiraFieldMappersKeys.Date;
  validCustomKeys = [
    'com.atlassian.jira.plugin.system.customfieldtypes:datepicker',
    'com.atlassian.jira.plugin.system.customfieldtypes:datetime'
  ];
  validTypes = [JiraTypeDate, JiraTypeDateTime];
  isEnum = false;

  canMapThisField(jiraSchema: IssueTypeField): boolean {
    const { type, custom } = jiraSchema.schema;
    const isDate = this.validTypes.includes(type);
    const isCustom = !!custom;

    return isCustom ? isDate && this.validCustomKeys.includes(custom) : isDate;
  }

  formatDateStringToHTMLRequirements(
    dateAsString: string,
    uiFieldType: UIFieldInputType
  ): string {
    if (!dateAsString || dateAsString.length === 0) {
      return '';
    }

    const date = new Date(dateAsString);
    if (isNaN(date.valueOf())) {
      return '';
    }

    return uiFieldType === 'date'
      ? date.toISOString().slice(0, 10) // Returns 'YYYY-MM-DD'
      : date.toISOString().slice(0, 19); // Returns 'YYYY-MM-DDTHH:MM'
  }

  getDisplayValue(
    fieldSchema: IssueTypeField,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    axeIssue: any,
    fieldMapping?: MappingDefinition
  ): { value?: string; uiFieldType: UIFieldInputType } {
    const { type } = fieldSchema.schema;
    let uiFieldType: UIFieldInputType;
    if (type === JiraTypeDate) {
      uiFieldType = 'date';
    } else {
      uiFieldType = 'datetime-local';
    }

    // If we have a mapping, use it and ignore all else
    if (fieldMapping && fieldMapping.sourceType === IssueFieldSource.Axe) {
      return {
        value: this.formatDateStringToHTMLRequirements(
          axeIssue[fieldMapping.source] as string,
          uiFieldType
        ),
        uiFieldType: uiFieldType
      };
      // Of if there is a default value, use it
    } else if (fieldSchema.hasDefaultValue && fieldSchema.defaultValue) {
      return {
        value: this.formatDateStringToHTMLRequirements(
          fieldSchema.defaultValue as string,
          uiFieldType
        ),
        uiFieldType: uiFieldType
      };
    }

    return {
      uiFieldType: uiFieldType
    };
  }

  // Jira need a specific formating for datetime with +0000 at the end
  formatDateWithOffset(date: Date, uiFieldType: UIFieldInputType) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (uiFieldType === JiraTypeDate) {
      return `${year}-${month}-${day}`;
    } else {
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000+0000`;
    }
  }

  convertDisplayToIntegrationValue(uiFieldDefinition: UIFieldDefinition): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    integrationValue: any;
    error?: UiFieldValidationError;
  } {
    // Check for a case when there is no value provided
    if (!uiFieldDefinition.value || uiFieldDefinition.value === '') {
      if (uiFieldDefinition.rawSchema.required) {
        return {
          integrationValue: undefined,
          error: {
            ...defaultWithoutError,
            isMissingRequired: true
          }
        };
      } else {
        return {
          integrationValue: undefined,
          error: defaultWithoutError
        };
      }
    }

    // If value is provided, check that it's valid
    const date = new Date(uiFieldDefinition.value);
    if (isNaN(date.valueOf())) {
      return {
        integrationValue: undefined,
        error: {
          ...defaultWithoutError,
          isInvalidValue: true
        }
      };
    } else {
      return {
        integrationValue: this.formatDateWithOffset(
          date,
          uiFieldDefinition.uiFieldType
        ),
        error: defaultWithoutError
      };
    }
  }
}
